/**
 * Unified domain taxonomy — single source of truth shared by the architecture
 * map (apps/assessment/src/presentation/data/architecture-components.ts) and
 * both roadmap boards (apps/assessment/src/presentation/data/roadmaps.ts).
 *
 * Replaces the prior parallel taxonomies: architecture's 7-domain set and the
 * roadmap's 9-track set. See agent-os/specs/2026-05-17-1730-unified-domain-taxonomy.
 */

import type { FeatureDomain } from "@/infrastructure/supabase/database.types";

export type Domain = FeatureDomain;

/** Canonical display order — used by the architecture legend and the stack/picker. */
export const DOMAINS = [
  "supply-chain",
  "catalog-content",
  "pricing-commerce",
  "fulfillment-ops",
  "customer-experience",
  "finance",
  "app-core",
  "data-bi",
] as const satisfies readonly Domain[];

export const DOMAIN_LABEL: Record<Domain, string> = {
  "supply-chain": "Supply Chain",
  "catalog-content": "Catalog / Content",
  "pricing-commerce": "Pricing / Commerce",
  "fulfillment-ops": "Fulfillment / Ops",
  "customer-experience": "Customer Experience",
  finance: "Finance",
  "app-core": "App Core",
  "data-bi": "Data / BI",
};

export const DOMAIN_DESCRIPTION: Record<Domain, string> = {
  "supply-chain": "Sourcing, vendors, warehouse intake (GS1, Merlog, Woody).",
  "catalog-content": "Product catalog, PIM, translations, CMS content.",
  "pricing-commerce": "Pricing rules, promotions, coupons, basket discounts.",
  "fulfillment-ops": "WMS, dispatch, driver ops, shift planning.",
  "customer-experience": "Support, CRM, communication, agent consoles.",
  finance: "Payments, accounting, credit, settlement.",
  "app-core": "Customer-facing apps and platform integrations.",
  "data-bi": "Analytics, BI dashboards, segmentation, warehouse.",
};

export const DOMAIN_COLOR: Record<Domain, string> = {
  "supply-chain": "oklch(0.68 0.14 60)", // amber
  "catalog-content": "oklch(0.55 0.17 190)", // cyan
  "pricing-commerce": "oklch(0.62 0.16 50)", // orange
  "fulfillment-ops": "oklch(0.55 0.16 145)", // green
  "customer-experience": "oklch(0.58 0.17 350)", // magenta
  finance: "oklch(0.62 0.14 90)", // gold
  "app-core": "oklch(0.55 0.16 260)", // indigo
  "data-bi": "oklch(0.55 0.16 285)", // violet
};

export const DOMAIN_COLOR_SOFT: Record<Domain, string> = {
  "supply-chain": "oklch(0.92 0.07 60)",
  "catalog-content": "oklch(0.92 0.07 190)",
  "pricing-commerce": "oklch(0.94 0.06 50)",
  "fulfillment-ops": "oklch(0.92 0.07 145)",
  "customer-experience": "oklch(0.92 0.07 350)",
  finance: "oklch(0.92 0.07 90)",
  "app-core": "oklch(0.92 0.07 260)",
  "data-bi": "oklch(0.92 0.06 285)",
};

export function isDomain(value: string): value is Domain {
  return (DOMAINS as readonly string[]).includes(value);
}
